import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { BillingProvider } from "@/Common/Services/ExternalBilling/BillingProvider";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { PatientBillingHistory } from "./PatientBillingHistory/PatientBillingHistory";
import { BillsSummarySection } from "./BillsSummarySection";

interface IBillingClaimSectionProps {
    noteModel: IPatientMedicalNoteModel;
    isReadOnly: boolean;
    patientId: string;
    billingProvider: BillingProvider;
    onCreateNewClaimCallBack: () => Promise<void>;
    usePatientBillingHistory: boolean;
}

const renderBills = (props: IBillingClaimSectionProps): JSX.Element => (
    <FeatureFlagControl
        flag={props.usePatientBillingHistory}
        activeFeature={(
            <PatientBillingHistory
                isReadOnly={props.isReadOnly}
                encounterSiteId={props.noteModel.get("Encounter").SiteId}
            />
        )}
        fallbackFeature={(
            <BillsSummarySection
                encounterId={props.noteModel.get("Encounter").Id}
                patientId={props.patientId}
                medfarBills={props.noteModel.get("EncounterMedfarBillsLight") ?? []}
                facturationBills={props.noteModel.get("FacturationNetBills")?.toArray() ?? []}
                externalClaims={props.noteModel.get("ExternalBillingClaims")?.toArray() ?? []}
                isReadonly={props.isReadOnly}
            />
        )}
    />
);

function BillingClaimSection(props: Readonly<IBillingClaimSectionProps>): JSX.Element {
    const resolvedBillingProvider = sessionStore.getCurrentEmployee().getBillingProviderResolved();
    const hasSoftInfoClaimForEmployee = props.noteModel.get("ExternalBillingClaims")?.some(p => p.BillingProvider == BillingProvider.SoftInfo && p.CreatedByEmployeeId == sessionStore.getCurrentEmployee().ContactFacetId);
    const hasFacturationNetClaimForEmployee = props.noteModel.get("FacturationNetBills")?.some(p => p.CreatedByEmployeeId == sessionStore.getCurrentEmployee().ContactFacetId);

    const canCreateNewClaim = props.billingProvider !== null
        && (resolvedBillingProvider !== BillingProvider.SoftInfo || !hasSoftInfoClaimForEmployee)
        && (props.billingProvider !== BillingProvider.FacNet || !hasFacturationNetClaimForEmployee)
        && !props.isReadOnly;

    const billingProviderName = `-${BillingProvider[resolvedBillingProvider]}`;

    return (
        <div className="note-block" id="medical-note-billing-container">
            <div className="patient-note padding-none-top">
                <table>
                    <tbody>
                        <tr>
                            <td className="c1">
                                <div className="display-flex-justified-end">
                                    {sessionStore.isScheduledVisitChecked != null && (
                                        <div className={"add-btn-column margin-md-right" + (props.isReadOnly ? " no-hover" : "")}>
                                            <a
                                                onClick={() =>
                                                    sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.ShowVisitTypeSelectionPopup, {
                                                        callback: () => patientStore.EmitChangeToListeners()
                                                    })
                                                }
                                            >
                                                {sessionStore.isScheduledVisitChecked ? "(RDV)" : "(SRV)"}
                                            </a>
                                        </div>
                                    )}
                                    <strong data-cy={`medicalNote-paymentRequest${!IsNullOrUndefined(resolvedBillingProvider) ? billingProviderName : ""}`}>
                                        {cacheStore.GetString("PaymentRequestLabel")}
                                    </strong>
                                </div>
                            </td>
                            <td className="c2 bottom-pad">
                                <div className="tb-row tb">
                                    {canCreateNewClaim && (
                                        <div className="add-btn-column">
                                            <a onClick={() => props.onCreateNewClaimCallBack()}>[+]</a>
                                        </div>
                                    )}
                                    <div className="note-items-column">
                                        {renderBills(props)}
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export { BillingClaimSection };
